import { UseMutateAsyncFunction, UseMutateFunction, useMutation } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';

import { RequestStatus } from '@telialt/models/src/RequestModel';
import { useHybrisSegment } from '@telialt/utils/src/segment';

import { submitLead, submitSmsConfirm } from '../api';
import { LeadFormProps, LeadSmsConfirmRequestData, LeadSubmitRequestData, LeadSubmitResponse } from '../model';

type MutateFunctionType<P> = UseMutateFunction<AxiosResponse<LeadSubmitResponse, any>, unknown, P, unknown>;
type MutateAsyncFunctionType<P> = UseMutateAsyncFunction<AxiosResponse<LeadSubmitResponse, any>, unknown, P, unknown>;

type UseLeadSubmitReturn = {
    mutateSubmit: MutateFunctionType<LeadSubmitRequestData>;
    mutateSubmitAsync: MutateAsyncFunctionType<LeadSubmitRequestData>;
    mutateSmsConfirm: MutateFunctionType<LeadSmsConfirmRequestData>;
    resetSubmit: () => void;
    resetSmsConfirm: () => void;
    status: RequestStatus;
    isSmsConfirm: boolean;
    isInvalidSmsConfirm: boolean | null;
};

export const useLeadSubmit = (onSubmit?: LeadFormProps['onSubmit']): UseLeadSubmitReturn => {
    const segment = useHybrisSegment();

    const {
        mutate: mutateSubmit,
        mutateAsync: mutateSubmitAsync,
        data: submitResponse,
        status: submitStatus,
        reset: resetSubmit,
    } = useMutation<AxiosResponse<LeadSubmitResponse>, unknown, LeadSubmitRequestData>({
        mutationFn: (data) => submitLead(data, segment),
        onSuccess: ({ data }) => {
            onSubmit?.(data.leadId);
        },
    });

    const {
        mutate: mutateSmsConfirm,
        data: smsConfirmResponse,
        error: smsConfirmError,
        status: confirmStatus,
        reset: resetSmsConfirm,
    } = useMutation({ mutationFn: submitSmsConfirm });

    const response = smsConfirmResponse || submitResponse;

    let status: RequestStatus = RequestStatus.Idle;
    if (submitStatus === RequestStatus.Pending || confirmStatus === RequestStatus.Pending) {
        status = RequestStatus.Pending;
    } else if (
        (confirmStatus === RequestStatus.Success || submitStatus === RequestStatus.Success) &&
        response?.data?.data === 'ok'
    ) {
        status = RequestStatus.Success;
    } else if (
        confirmStatus === RequestStatus.Error ||
        submitStatus === RequestStatus.Error ||
        response?.data?.data === 'error'
    ) {
        status = RequestStatus.Error;
    }

    const isSmsConfirm = submitStatus === RequestStatus.Success && submitResponse?.data?.data === 'sms';
    const isInvalidSmsConfirm =
        status === RequestStatus.Error &&
        smsConfirmError &&
        axios.isAxiosError(smsConfirmError) &&
        smsConfirmError.response?.status === 400;

    return {
        mutateSubmit,
        mutateSubmitAsync,
        mutateSmsConfirm,
        resetSubmit,
        resetSmsConfirm,
        status,
        isSmsConfirm,
        isInvalidSmsConfirm,
    };
};
